import { FormModal, SubmitProps } from '@/components/form-modal.js';

import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  buildEmailsSentSelectOption,
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  emptySelectOption,
} from '@/components/ui/multi-select-utils.js';
import {
  createStudentGroup,
  getCoaches,
  getStudentGroup,
  getStudentGroups,
  getStudentGroupsAttendance,
  updateStudentGroup,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { adjustMinimalClassTimeAcrossTZ, EmailsSent, StudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AllUpdateTeacherTypeValues } from '@/lib/coach-utils.js';
import { formCreateEditClassSchema } from '@/lib/student-group-utils.js';
import { protectedPeriodOverrideFromForm } from '@/routes/_layout/student-groups/-components/protected-period-override-fields.js';
import { assertValidPlainDateIntervalLike, FrequentTimeZones } from '@airreading/common';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import { CreateEditClassForm } from './create-edit-class-form.js';
import { nullIfString, stringIfNull } from '@/lib/string-utils.js';

interface CreateEditClassProps {
  trigger: ReactNode;
  schoolId: string;
  from?: StudentGroupWithScheduleAndLevel;
  schoolTimeZone: string;
  fromAttendance?: boolean;
}

export const CreateEditClass = ({
  trigger,
  schoolId,
  from,
  schoolTimeZone,
  fromAttendance = false,
}: CreateEditClassProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const attendancePolicyV2AdminEnabled = useFeatureFlag('teacher-attendance-policy-v2-admin', false);

  const { mutateAsync: createMutate } = useMutation({
    mutationFn: createStudentGroup,
  });

  const { mutateAsync: updateMutate } = useMutation({
    mutationFn: updateStudentGroup,
  });

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formCreateEditClassSchema>>) => {
    const {
      coach,
      students,
      overrideStartPlainDate,
      overrideEndPlainDate,
      classTime,
      learningPath,
      startingLesson,
      teacherType,
      coachAvailable,
      coachAll,
      attendanceStatus,
      emailsSent,
      coachUnassignedAtPlainDate,
      attendanceNotes,
      notes,
    } = formData;

    if (!fromAttendance) {
      if (Boolean(overrideStartPlainDate) !== Boolean(overrideEndPlainDate)) {
        toast({
          title: 'Invalid date range',
          description: 'Both start and end dates must be provided or neither.',
        });
        return;
      }

      if (overrideStartPlainDate && overrideEndPlainDate) {
        try {
          assertValidPlainDateIntervalLike(overrideStartPlainDate, overrideStartPlainDate);
        } catch (e: unknown) {
          if (e instanceof RangeError) {
            toast({
              title: 'Invalid date range',
              description: 'Start date cannot be after end date.',
            });
            return;
          }
        }
      }
    }

    const selectedFromCoach = teacherType === AllUpdateTeacherTypeValues.Available ? coachAvailable : coachAll;
    const hasNoCoach = !selectedFromCoach || !selectedFromCoach.value;

    if (from && !fromAttendance && hasNoCoach) {
      toast({
        title: 'Invalid teacher',
        description: 'Please select a teacher for the class.',
      });
      return;
    }

    const selectedCoach = from ? selectedFromCoach || coach : coach;

    const data = fromAttendance
      ? {
          status: from!.status,
          coachId: from!.coach?.id,
          studentIds: from!.students.map((student) => student.id),
          overrideStartPlainDate: from!.overrideStartPlainDate ?? null,
          overrideEndPlainDate: from!.overrideEndPlainDate ?? null,
          classTimeId: from!.classTime.id,
          learningPathId: from!.learningPath.id,
          startingLessonId: from!.startingLesson.id,
          attendanceStatus:
            attendancePolicyV2AdminEnabled && from ? from.attendanceStatus ?? null : nullIfString(attendanceStatus),
          emailsSent: emailsSent.map((email) => email.value as EmailsSent),
          coachUnassignedAtPlainDate,
          attendanceNotes: attendanceNotes ?? null,
          notes: notes ?? null,
        }
      : {
          status: from?.status || 'active',
          coachId: selectedCoach?.value,
          studentIds: students.map((student) => student.value),
          overrideStartPlainDate,
          overrideEndPlainDate,
          classTimeId: classTime.value,
          learningPathId: learningPath.value,
          startingLessonId: startingLesson.value,
          attendanceStatus: nullIfString(attendanceStatus),
          emailsSent: emailsSent.map((email) => email.value as EmailsSent),
          coachUnassignedAtPlainDate,
          attendanceNotes: attendanceNotes ?? null,
          notes: notes ?? null,
          protectedPeriodOverride: protectedPeriodOverrideFromForm(formData),
        };

    closeModal();

    const apiCall = from ? () => updateMutate({ id: from.id, data }) : () => createMutate(data);

    return apiCall()
      .then(() => {
        if (from) {
          queryClient.invalidateQueries(getStudentGroup(from.id));
          queryClient.invalidateQueries(getCoaches());
        } else {
          queryClient.invalidateQueries(getStudentGroups());
        }

        if (fromAttendance) {
          queryClient.invalidateQueries(getStudentGroupsAttendance());
        }

        queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });

        toast({
          title: `Class ${from ? 'updated' : 'created'} successfully`,
        });
        closeModal();
      })
      .catch((error) => {
        toast({
          title: `Error ${from ? 'updating' : 'creating'} class`,
          description: airServerErrorFrom(error)?.userFacingMessage || '',
        });
      });
  };

  const defaultValues: DefaultValues<z.infer<typeof formCreateEditClassSchema>> = {
    coach: from?.coach ? buildUserSelectOptions(from.coach) : emptySelectOption,
    students: from?.students ? from.students.map(buildUserSelectOptions) : [],
    schedule: from?.classTime.schedule
      ? buildNameLikeSelectOptions({ id: from.classTime.schedule.id, name: from.classTime.schedule.name })
      : emptySelectOption,
    classTime: from?.classTime
      ? buildNameLikeSelectOptions({
          id: from.classTime.id,
          name: formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(from.classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
          ),
        })
      : emptySelectOption,
    overrideStartPlainDate: from?.overrideStartPlainDate ? from.overrideStartPlainDate : undefined,
    overrideEndPlainDate: from?.overrideEndPlainDate ? from.overrideEndPlainDate : undefined,
    learningPath: from?.learningPath ? buildNameLikeSelectOptions(from.learningPath) : emptySelectOption,
    startingLesson: from?.startingLesson ? buildNameLikeSelectOptions(from.startingLesson) : emptySelectOption,
    teacherType: AllUpdateTeacherTypeValues.Available,
    coachAvailable: from?.coach ? buildUserSelectOptions(from.coach) : emptySelectOption,
    attendanceStatus: stringIfNull(from?.attendanceStatus),
    emailsSent: from?.emailsSent ? from.emailsSent.map(buildEmailsSentSelectOption) : [],
    coachUnassignedAtPlainDate: from?.coachLastUnassignedAtPlainDate ? from.coachLastUnassignedAtPlainDate : undefined,
    attendanceNotes: from?.attendanceNotes,
    notes: from?.notes,
  };

  return (
    <FormModal
      modalTitle={`${from ? 'Edit' : 'Add'}${!fromAttendance ? ' Class' : ''}`}
      formSchema={formCreateEditClassSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel={from ? 'Save' : 'Add'}
      validateOnOpen={!!from}
    >
      {({ form }) => {
        return <CreateEditClassForm form={form} schoolId={schoolId} from={from} fromAttendance={fromAttendance} />;
      }}
    </FormModal>
  );
};
